import TextInput from '../TextInput';
import TextArea from '../TextArea.jsx';
import FileUpload from '../FileUpload';
import RedeemableForInput from './RedeemableForInput.jsx';
import ExpirationInput from './ExpirationInput.jsx';
import React, { useEffect, useMemo, useState } from 'react';
import {
    setPackagesQuery,
    useEventTypesQuery,
    usePackageDetailsQuery,
    usePackagesQuery,
} from '../../lib/Queries';
import * as Toast from '../../lib/Toast.js';
import useBusinessNavigate from '../../lib/useBusinessNavigate.js';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import Loading from '../Loading';
import ErrorMessageBlock from '../ErrorMessageBlock.jsx';
import { formatCurrency } from '../../lib/CurrencyHelpers.js';
import { minutesToDuration } from '../../lib/DateTime.js';
import {
    humanRedeemableAmount,
    PackageTypes,
} from '../../lib/PackageHelpers.js';
import MoneyInput from '../MoneyInput.jsx';
import { ucfirst } from '~/lib/Utils';
import InputPackageDetails from '~/components/packages/InputPackageDetails';
import getRedeemableError from '~/components/packages/getRedeemableForError';
import Select from '~/components/Select';
import TextInputWithDropdown from '~/components/TextInputWithDropdown';
import Toggle from '~/components/Toggle';
import InputSubscriptionAdditionalSettings from '~/components/packages/InputSubscriptionAdditionalSettings';
import InfoMessageBlock from '~/components/InfoMessageBlock';

const renewalBehaviorOptions = {
    reset: 'Reset the value every renewal',
    rollover: 'Rollover balance on renewal',
    asis: 'Do not grant more appointments on renewal',
};

const PackageEditForm = ({
    packageId,
    onChangeName,
    singular,
    basePath,
    type,
}) => {
    const navigate = useBusinessNavigate();

    const { data: packageData, isLoading } = usePackageDetailsQuery(packageId);
    const { data: eventTypesData } = useEventTypesQuery();
    const eventTypes = eventTypesData || [];

    const { save, isSaving } = useBusinessPostApi(`/packages/${packageId}`);
    const { data: eventTypeData } = useEventTypesQuery();

    const [data, setData] = useState({
        type: type,
        redeemable_for: {
            type: 'minutes',
            amount: 90,
        },
    });

    function setSubscriptionSettings(value) {
        setData(old => {
            let newValue = value;
            if (typeof value === 'function') {
                newValue = value(old?.subscription_settings);
                console.log('setting', newValue, old?.subscription_settings);
            }

            return {
                ...old,
                subscription_settings: newValue,
            };
        });
    }

    const isNew = packageId === '0';
    const isRedemptionDirty =
        isNew ||
        JSON.stringify(data?.redeemable_for) !==
            JSON.stringify(packageData?.redeemable_for) ||
        JSON.stringify(data?.subscription_settings) !==
            JSON.stringify(packageData?.subscription_settings);
    const isExpirationDirty =
        Math.max(parseInt(data?.expires?.expires) || -9999, -1) !==
        Math.max(parseInt(packageData?.expires?.expires) || -9999, -1);

    useEffect(() => {
        if (data?.id !== packageId && packageData) {
            // Set the initial values
            if (packageData) {
                setData(packageData);
            }
        }
    }, [packageId, JSON.stringify(packageData)]);

    useEffect(() => {
        if (isNew && !data?.redeemable_for?.eventTypes?.length) {
            // Default to all checked
            setData(old => ({
                ...old,
                redeemable_for: {
                    ...old?.redeemable_for,
                    eventTypes: (eventTypeData || []).map(et => et.id),
                },
            }));
        }
    }, [eventTypeData?.length]);

    function submit(ev) {
        ev.preventDefault();

        save(data).then(result => {
            setPackagesQuery(result, type);
            navigate(basePath);
            if (result?.form) {
                Toast.success(
                    `Successfully saved ${singular}. The form "${result.form.name}" can be used by clients to purchase this package.`,
                );
            } else {
                Toast.success(`Successfully saved ${singular}`);
            }
        });
    }

    const redeemableForError = getRedeemableError(
        eventTypes,
        data?.redeemable_for,
    );

    useEffect(() => {
        onChangeName(data?.name);
    }, [data?.name]);

    if (isLoading) {
        return <Loading />;
    }

    return (
        <form onSubmit={submit} className="mt-6  pb-16">
            <div className="panel-label">{ucfirst(singular)} Details</div>
            <div className="panel p-6">
                <InputPackageDetails
                    data={data}
                    setData={setData}
                    type={type}
                />
            </div>

            <div className="panel-label mt-6">Value</div>
            <div className="panel p-6">
                <RedeemableForInput
                    value={data?.redeemable_for}
                    onChange={newValue =>
                        setData(old => ({ ...old, redeemable_for: newValue }))
                    }
                    isNew={isNew}
                />
                {!!redeemableForError && (
                    <ErrorMessageBlock className="mt-2">
                        {redeemableForError}
                    </ErrorMessageBlock>
                )}

                {type === PackageTypes.SUBSCRIPTION && (
                    <Select
                        label="Behavior When Renewed"
                        options={renewalBehaviorOptions}
                        value={data?.subscription_settings?.renewalBehavior}
                        onChange={newValue =>
                            setSubscriptionSettings(old => ({
                                ...old,
                                renewalBehavior: newValue,
                            }))
                        }
                        className="mt-4"
                    />
                )}
            </div>

            {type === PackageTypes.SUBSCRIPTION && (
                <InputSubscriptionAdditionalSettings
                    data={data}
                    setData={setData}
                    setSubscriptionSettings={setSubscriptionSettings}
                />
            )}

            {type !== PackageTypes.SUBSCRIPTION && (
                <>
                    <div className="panel-label mt-6">Expires</div>
                    <div className="panel">
                        <ExpirationInput
                            value={data?.expires}
                            onChange={newValue =>
                                setData(old => ({ ...old, expires: newValue }))
                            }
                        />
                    </div>
                </>
            )}

            {!isNew && isRedemptionDirty && (
                <InfoMessageBlock showIcon={true} className="mt-6 max-w-2xl">
                    Warning, changing this {singular} will change it for all
                    clients who have already purchased this.
                </InfoMessageBlock>
            )}

            {!isNew && isExpirationDirty && (
                <InfoMessageBlock showIcon={true} className="mt-6 max-w-2xl">
                    The updated expiration will be applied to new {singular}{' '}
                    purchases only, existing ones will not be updated.
                </InfoMessageBlock>
            )}

            <div className="fixed bottom-0 py-2 bg-gray-100 w-full">
                <button type="submit" className="mb-6 btn-save">
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default PackageEditForm;
